import { useT } from "../../../shared/i18n/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Globe, Loader2 } from "lucide-react";
import { useState } from "react";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { useTRPC } from "../../../shared/api/trpc";
import { IntakeForm, intakeFieldTypes, type IntakeField, type IntakeSubmission } from "./intake-form";
import { Button } from "@/components/ui/button";

export function intakeFieldsForView(
  viewFields: Array<{ fieldDefinitionId: string; visible: boolean }>,
  definitions: WorkObjectFieldDefinition[],
  statusFieldDefinitionId: string | null,
): IntakeField[] {
  const byId = new Map(definitions.map((definition) => [definition.id, definition]));
  return viewFields
    .filter((viewField) => viewField.visible)
    .flatMap((viewField) => {
      const definition = byId.get(viewField.fieldDefinitionId);
      if (!definition || definition.key === "title" || definition.id === statusFieldDefinitionId) return [];
      if (!intakeFieldTypes.has(definition.type)) return [];
      return [
        {
          definition,
          id: definition.id,
          name: definition.name,
          options: (definition.options ?? []).map((option) => ({ color: option.color, id: option.id, value: option.value })),
          ratingScale: definition.type === "rating" ? (definition.config?.ratingScale as number | undefined) ?? 5 : null,
          required: definition.isRequired,
          type: definition.type,
        },
      ];
    });
}

// The in-app face of a form view: the same intake form the public link
// serves, plus the editor-only public-link controls.
export function FormViewSurface({
  fields,
  onCreate,
  statusFieldDefinitionId,
  view,
}: {
  fields: WorkObjectFieldDefinition[];
  onCreate: (input: IntakeSubmission) => Promise<void>;
  statusFieldDefinitionId: string | null;
  view: { fields: Array<{ fieldDefinitionId: string; visible: boolean }>; id: string; name: string };
}) {
  const t = useT();
  const intakeFields = intakeFieldsForView(view.fields, fields, statusFieldDefinitionId);
  const [submittedCount, setSubmittedCount] = useState(0);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5 py-6">
      <PublicLinkControls viewId={view.id} />
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <IntakeForm
          fields={intakeFields}
          key={submittedCount}
          onSubmit={async (input) => {
            await onCreate(input);
            setSubmittedCount((count) => count + 1);
          }}
          submitLabel={t("board.createItem")}
        />
      </div>
      <p className="text-xs text-muted-foreground/70">{t("forms.viewFieldsHint")}</p>
    </div>
  );
}

function PublicLinkControls({ viewId }: { viewId: string }) {
  const t = useT();
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const linkQuery = useQuery(trpcClient.forms.link.queryOptions({ boardViewId: viewId }, { retry: false }));
  const enableMutation = useMutation(trpcClient.forms.enableLink.mutationOptions());
  const disableMutation = useMutation(trpcClient.forms.disableLink.mutationOptions());
  const [isCopied, setIsCopied] = useState(false);

  // Viewers cannot manage the capability link — the query rejects for them
  // and the whole block stays hidden.
  if (linkQuery.isError || linkQuery.isPending) return null;

  const token = linkQuery.data.token;
  const url = token ? `${window.location.origin}/form/${token}` : null;
  const busy = enableMutation.isPending || disableMutation.isPending;

  async function toggle(enabled: boolean) {
    if (enabled) await enableMutation.mutateAsync({ boardViewId: viewId });
    else await disableMutation.mutateAsync({ boardViewId: viewId });
    await queryClient.invalidateQueries({ queryKey: trpcClient.forms.link.queryKey({ boardViewId: viewId }) });
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/70 bg-card px-4 py-3">
      <Globe className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t("shareLink.publicLink")}</p>
        <p className="truncate text-xs text-muted-foreground">
          {url ?? t("shareLink.anyoneCanSubmit")}
        </p>
      </div>
      {url ? (
        <Button
          className="shrink-0"
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setIsCopied(true);
            window.setTimeout(() => setIsCopied(false), 1500);
          }}
          size="sm"
          variant="ghost"
        >
          {isCopied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
          Copy
        </Button>
      ) : null}
      <Button className="shrink-0" disabled={busy} onClick={() => void toggle(token === null)} size="sm" variant={token ? "outline" : "default"}>
        {busy ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
        {token ? t("shareLink.disable") : t("shareLink.enable")}
      </Button>
    </div>
  );
}
